import React, { useMemo } from "react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import useAsyncSearch from "../../../../hooks/useAsyncSearch";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const SelectFIeld = ({ nameApiOptions, valueKey, labelKey, labelKey2, placeholder, value, setValue, isMulti = false, disabled = false, options }) => {
    const { asyncOptions } = useAsyncSearch(nameApiOptions);
    
    // Determine if we should use static Select or AsyncSelect
    const isStaticOptions = options && Array.isArray(options) && !nameApiOptions;

    // Find the selected value object from options if value is just an ID
    const selectedValue = useMemo(() => {
        if (!value) return null;

        // If value is already an object, return it
        if (typeof value === 'object') {
            return value;
        }

        // If value is a primitive (ID), find the matching option
        if (options && Array.isArray(options)) {
            const found = options.find(opt => opt[valueKey] === value);
            if (found) return found;
        }

        // If not found in options, return null (will show placeholder)
        return null;
    }, [value, options, valueKey]);

    const getLabel = (option) => {
        if (!option) return "";
        const label1 = option[labelKey] || "";
        const label2 = labelKey2 && option[labelKey2] ? ` ${option[labelKey2]}` : "";
        return `${label1}${label2}`.trim();
    };

    // Custom styles to match vehicle filter design
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: 'white',
            border: state.isFocused ? '1px solid #1a4bbd' : '1px solid #d0d5dd',
            borderRadius: '8px',
            minHeight: '44px',
            boxShadow: state.isFocused ? '0 0 0 4px rgba(26, 75, 189, 0.1)' : '0 1px 2px rgba(16, 24, 40, 0.05)',
            '&:hover': {
                borderColor: '#1a4bbd',
            },
            cursor: 'pointer',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '2px 8px',
            paddingLeft: '40px',
        }),
        input: (provided) => ({
            ...provided,
            margin: '0',
            padding: '0',
            color: '#344054',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#98a2b3',
            fontSize: '14px',
            fontWeight: '400',
            paddingLeft: '40px',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#344054',
            fontSize: '14px',
            fontWeight: '400',
            paddingLeft: '40px',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? '#1a4bbd' : '#98a2b3',
            padding: '8px',
            '&:hover': {
                color: '#1a4bbd',
            }
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: '#98a2b3',
            padding: '8px',
            '&:hover': {
                color: '#667085',
            }
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #eaecf0',
            marginTop: '4px',
            overflow: 'hidden',
            zIndex: 100,
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#1a4bbd' : state.isFocused ? '#f2f4f7' : 'white',
            color: state.isSelected ? 'white' : '#344054',
            fontSize: '14px',
            padding: '10px 12px',
            paddingLeft: '40px',
            cursor: 'pointer',
            borderRadius: '6px',
            margin: '2px 0',
            '&:active': {
                backgroundColor: '#1a4bbd',
            }
        }),
        noOptionsMessage: (provided) => ({
            ...provided,
            color: '#98a2b3',
            fontSize: '14px',
            padding: '12px',
        }),
        loadingMessage: (provided) => ({
            ...provided,
            color: '#98a2b3',
            fontSize: '14px',
            padding: '12px',
        }),
    };

    const commonProps = {
        isMulti,
        isClearable: true,
        getOptionValue: (option) => option[valueKey],
        getOptionLabel: getLabel,
        placeholder,
        onChange: (e) => {
            setValue(e ? e : null);
        },
        value: selectedValue,
        isDisabled: disabled,
        styles: customStyles,
        isSearchable: true,
        menuPortalTarget: isMobile ? null : document.body,
        menuPosition: isMobile ? "absolute" : "fixed",
    };

    // Use static Select for options array, AsyncSelect for API calls
    if (isStaticOptions) {
        return (
            <Select
                {...commonProps}
                options={options}
            />
        );
    }

    return (
        <AsyncSelect
            {...commonProps}
            defaultOptions={true}
            cacheOptions
            loadOptions={asyncOptions}
        />
    );
};

export default SelectFIeld;



